(() => {
  const STORAGE_KEY = 'elf_decors_favorites_v1';
  const PAGE_PATH = '/pages/favorites';
  const TOAST_DURATION = 5000;

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.warn('ELF DECORS favorites could not be saved.', error);
    }
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  function getItems() {
    return read();
  }

  function isSaved(handle) {
    return getItems().some((item) => item.handle === handle);
  }

  function syncButtons() {
    const items = getItems();
    document.querySelectorAll('[data-favorite-button]').forEach((button) => {
      const saved = items.some((item) => item.handle === button.dataset.favoriteHandle);
      button.classList.toggle('is-active', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-label', saved ? 'Remove from favorites' : 'Add to favorites');
      const icon = button.querySelector('.elf-favorite-heart');
      if (icon) icon.classList.toggle('is-filled', saved);
    });
  }

  function ensureToast() {
    let toast = document.querySelector('[data-favorites-toast]');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'elf-favorites-toast';
    toast.setAttribute('data-favorites-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.hidden = true;
    document.body.appendChild(toast);
    return toast;
  }

  let toastTimer;
  function showToast(item, added = true) {
    const toast = ensureToast();
    clearTimeout(toastTimer);
    const image = item.image || '';
    toast.innerHTML = `
      <div class="elf-favorites-toast__top">
        <div class="elf-favorites-toast__copy">
          <strong>${added ? 'Added to favorites' : 'Removed from favorites'}</strong>
          ${item.title ? `<span>${escapeHtml(item.title)}</span>` : ''}
        </div>
        ${image ? `<img src="${escapeHtml(image)}" alt="" class="elf-favorites-toast__image" loading="eager">` : ''}
      </div>
      ${added ? `<a class="elf-favorites-toast__link" href="${PAGE_PATH}">View Favorites <span aria-hidden="true">→</span></a>` : ''}
    `;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => { toast.hidden = true; }, 240);
    }, TOAST_DURATION);
  }

  function toggleFavorite(button) {
    const handle = button.dataset.favoriteHandle;
    if (!handle) return;
    const items = getItems();
    const index = items.findIndex((item) => item.handle === handle);
    const item = {
      handle,
      id: button.dataset.favoriteId || '',
      title: button.dataset.favoriteTitle || '',
      url: button.dataset.favoriteUrl || `/products/${handle}`,
      image: button.dataset.favoriteImage || '',
      savedAt: Date.now()
    };

    if (index >= 0) {
      const removed = items.splice(index, 1)[0];
      write(items);
      showToast(removed || item, false);
    } else {
      items.unshift(item);
      write(items);
      showToast(item, true);
    }
    syncButtons();
    renderFavoritesPage();
  }

  async function fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Product request failed');
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  function productPrice(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    const available = product.variants.filter((variant) => variant.available !== false);
    const variants = available.length ? available : product.variants;
    let lowest = null;
    let sale = null;
    variants.forEach((variant) => {
      const price = Number(variant.price);
      const compare = Number(variant.compare_at_price || 0);
      if (lowest === null || price < lowest) lowest = price;
      if (compare > price && (!sale || price < sale.price)) {
        sale = { price, compare };
      }
    });
    return sale || (lowest !== null ? { price: lowest, compare: 0 } : null);
  }

  async function renderFavoritesPage() {
    const grid = document.querySelector('[data-favorites-grid]');
    const empty = document.querySelector('[data-favorites-empty]');
    if (!grid || !empty) return;

    const items = getItems();
    if (!items.length) {
      grid.innerHTML = '';
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    grid.innerHTML = items.map((item) => `
      <article class="elf-favorite-card" data-favorite-card="${escapeHtml(item.handle)}">
        <div class="elf-favorite-card__media">
          <a href="${escapeHtml(item.url)}" aria-label="${escapeHtml(item.title)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
          </a>
          <button type="button" class="elf-favorite-card__remove" data-favorite-remove="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)} from favorites">×</button>
        </div>
        <div class="elf-favorite-card__info">
          <a class="elf-favorite-card__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          <div class="elf-favorite-card__price" data-favorite-price="${escapeHtml(item.handle)}">${money(0)}</div>
        </div>
      </article>
    `).join('');

    await Promise.all(items.map(async (item) => {
      const product = await fetchProduct(item.handle);
      const price = productPrice(product);
      const priceEl = grid.querySelector(`[data-favorite-price="${CSS.escape(item.handle)}"]`);
      if (!priceEl || !price) return;
      const currency = product?.currency || window.Shopify?.currency?.active || 'USD';
      priceEl.innerHTML = price.compare > price.price
        ? `<s>${money(price.compare, currency)}</s> <strong>${money(price.price, currency)}</strong>`
        : `<strong>${money(price.price, currency)}</strong>`;
    }));
  }

  // Capture the favorite click before Shopify/theme card navigation handlers.
  // Product cards contain links, so the heart must always win and never navigate.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-favorite-button]');
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      toggleFavorite(button);
      return false;
    }

    const remove = event.target.closest('[data-favorite-remove]');
    if (remove) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const items = getItems().filter((item) => item.handle !== remove.dataset.favoriteRemove);
      write(items);
      renderFavoritesPage();
      syncButtons();
      return false;
    }
  }, true);

  document.addEventListener('DOMContentLoaded', () => {
    syncButtons();
    renderFavoritesPage();
  });

  window.ELFDecorsFavorites = {
    getItems,
    syncButtons,
    renderFavoritesPage,
    toggleFavorite
  };
})();
